import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      // IMPORTANT: session_id comes as URL FRAGMENT (#session_id=xxx), not query param
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;
      
      if (!sessionId) {
        setStatus('error');
        setError('Keine Session-ID gefunden');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const response = await fetch(`${API_URL}/api/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Authentifizierung fehlgeschlagen');
        }

        const data = await response.json();
        
        // Store JWT token
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        
        setStatus('success');
        toast.success(`Willkommen, ${data.user?.contact_name || data.user?.name || 'Kunde'}!`);
        
        // Clear the hash fragment and redirect to home
        window.history.replaceState(null, '', '/');
        setTimeout(() => navigate('/', { state: { user: data.user } }), 1500);
      } catch (err) {
        console.error('Auth error:', err);
        setStatus('error');
        setError(err.message || 'Authentifizierung fehlgeschlagen');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    processAuth();
  }, [navigate]);

  const BRAND = {
    turquoise: '#81CBD6',
    pink: '#F39DB2',
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div 
              className="w-16 h-16 mx-auto mb-6 rounded-full animate-spin"
              style={{ 
                border: '4px solid rgba(255,255,255,0.1)',
                borderTopColor: BRAND.turquoise 
              }}
            />
            <h1 className="text-2xl font-bold text-white mb-2">Anmeldung wird verarbeitet...</h1>
            <p className="text-gray-400">Bitte warten Sie einen Moment.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div 
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${BRAND.turquoise}20` }}
            >
              <svg className="w-8 h-8" style={{ color: BRAND.turquoise }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Erfolgreich angemeldet!</h1>
            <p className="text-gray-400">Sie werden weitergeleitet...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div 
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(239,68,68,0.2)' }}
            >
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Fehler bei der Anmeldung</h1>
            <p className="text-gray-400 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Sie werden zur Startseite weitergeleitet...</p>
          </>
        )}
      </div>
    </div>
  );
}
